import { differenceInMinutes } from 'date-fns';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import { calculateGoogleTimeframes } from './calculateGoogleTimeframes';
import { isOrderCancelled } from './isOrderCancelled';

// Estimated total route time (route start -> last non-final-return stop end),
// formatted as an "X.XX hours" string. Returns '' when it can't be determined.
export const calcEstimatedRouteTime = (
    route,
    { routeStartTime, preferences_start_estimate, preferences_warehouse_return_estimate, preferences_end_estimate } = {}
) => {
    if (!route) return '';

    const stopSequence = FTLStopHelpers.getStopSequence(route.stopsByRouteId);
    const containsOvernight = stopSequence.some((s) => s.overnight);

    const startTime =
        preferences_start_estimate || containsOvernight
            ? routeStartTime
            : stopSequence.find((s) => !s.start)?.stop_start_time || 0;

    // If the account has a default_end_location associated with it, then the end location IS used in regards to total delivery time, otherwise it is not.
    let lastNonFinalReturnStop;
    const reversedStops = stopSequence.reverse();
    if (preferences_warehouse_return_estimate && preferences_end_estimate) {
        lastNonFinalReturnStop = reversedStops[0];
    } else if (preferences_warehouse_return_estimate && !preferences_end_estimate) {
        lastNonFinalReturnStop = reversedStops?.find((stop) => !stop?.end);
    } else {
        lastNonFinalReturnStop = reversedStops?.find((stop) => !stop?.end && !FTLStopHelpers.isFinalDropoff(stop));
    }

    if (startTime && stopSequence.length > 1 && lastNonFinalReturnStop?.stop_end_time) {
        const timeEnd = new Date(lastNonFinalReturnStop.stop_end_time);
        const diff = differenceInMinutes(timeEnd, new Date(startTime));
        return `${(diff / 60).toFixed(2)} hours`;
    }

    return '';
};

export const routeIsMissingTimeframes = (route) => {
    return route?.stopsByRouteId?.some((stop) => {
        if (FTLStopHelpers.isFinalDropoff(stop) || stop.start || stop.end) {
            // pass check if stop is a final return stop, optimizeRoute does not create delwindow for final returns at the moment
            return false;
        }
        if (stop.overnight && FTLStopHelpers.isWarehousePickup(stop)) {
            return false;
        }
        return !stop.del_window_start || !stop.del_window_end;
    });
};

export const routeNeedsRecalculating = (route) => {
    const ordersByKey = Object.fromEntries((route.orders || []).map((mapping) => [mapping.order_id, mapping.order]));
    return (route.stopsByRouteId || []).some((stop) => {
        return (
            FTLStopHelpers.isCustomerStop(stop, route) &&
            (stop.orders || []).some((order_id) => ordersByKey[order_id]?.delivery_time_confirmed === 'recalculate')
        );
    });
};

export const routeIsConfirmed = (route) => {
    if (!route?.orders?.length || !route?.stopsByRouteId?.length || !route.sending_to) return false;
    const ordersByKey = Object.fromEntries((route.orders || []).map((mapping) => [mapping.order_id, mapping.order]));
    return (route.stopsByRouteId || []).every((s) => {
        if (!FTLStopHelpers.isCustomerStop(s, route)) {
            return true;
        }
        return (s.orders || []).every(
            (order_id) =>
                ordersByKey[order_id]?.order_status === 'cancelled' ||
                ordersByKey[order_id]?.delivery_time_confirmed === 'confirmed'
        );
    });
};

export { calculateGoogleTimeframes, isOrderCancelled };
